import React from 'react';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';

import { BrainCircuit, Lightbulb, BarChartHorizontal, Clock, AlertTriangle, Sparkles } from 'lucide-react';
import SEO from '@/components/SEO';

const recommendations = [
  { title: "Advanced Genkit Techniques", type: "Course", estTime: "3 hours", reason: "Builds on your current Genkit knowledge." },
  { title: "Firebase Security Rules Best Practices", type: "Docs", estTime: "1 hour", reason: "Identified gap based on project needs." },
  { title: "State Management Patterns in React", type: "Blog", estTime: "30 mins", reason: "Related to your recent React activity." },
];

const skillGaps = [
  { skill: "Advanced TypeScript", relevance: "High", suggestion: "Complete TypeScript Deep Dive course." },
  { skill: "Unit Testing Strategies", relevance: "Medium", suggestion: "Review Jest documentation & examples." },
  { skill: "CI/CD Pipeline Optimization", relevance: "Low", suggestion: "Explore GitHub Actions workflows." },
];

const anomaly = {
  detected: true,
  description: "Unusually high access to legacy API documentation (v1). Consider migrating usage or archiving.",
  severity: "Medium",
};

export default function AIInsights() {
  const { isMobile } = useSidebar();

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 bg-background text-foreground">
      <SEO title="AI Insights - Ankh Studio" description="Personalized AI-powered learning insights" />
        <div className="flex justify-between items-center mb-6 border-b border-border pb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-2">
              <BrainCircuit className="h-6 w-6 text-accent" /> AI Insights & Recommendations
            </h1>
            <p className="text-muted-foreground mt-1">Personalized guidance to accelerate your learning.</p>
          </div>
          {isMobile && <SidebarTrigger />}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 bg-card shadow-md border-border rounded-lg">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-accent" /> Recommended Next Steps
              </CardTitle>
              <CardDescription>AI suggestions based on your goals and activity.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {recommendations.map((rec, index) => (
                <div key={index} className="p-3 border rounded-md bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <h4 className="font-medium text-sm">{rec.title} <span className="text-xs text-muted-foreground">({rec.type})</span></h4>
                      <p className="text-xs text-muted-foreground mt-1">{rec.reason}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{rec.estTime}</p>
                       <Button variant="link" size="sm" className="h-auto p-0 mt-1 text-accent">View Resource</Button>
                    </div>
                  </div>
                </div>
              ))}
               <div className="text-center mt-4">
                 <Button variant="outline" size="sm">
                   <Sparkles className="h-4 w-4 mr-2 text-accent" /> Generate More Suggestions
                 </Button>
               </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="bg-card shadow-md border-border rounded-lg">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
                  <BarChartHorizontal className="h-5 w-5 text-accent" /> Skill Gap Analysis
                </CardTitle>
                <CardDescription>Areas identified for potential growth.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                 {skillGaps.map((gap, index) => (
                   <div key={index}>
                      <div className="flex justify-between items-center text-sm">
                         <span className="font-medium">{gap.skill}</span>
                         <span className={`text-xs px-2 py-0.5 rounded-full ${gap.relevance === 'High' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : gap.relevance === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'}`}>
                            {gap.relevance} Relevance
                         </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Suggestion: {gap.suggestion}</p>
                   </div>
                 ))}
              </CardContent>
            </Card>

             <Card className="bg-card shadow-md border-border rounded-lg">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-accent" /> Anomaly Detection
                </CardTitle>
                 <CardDescription>Unusual patterns in your activity.</CardDescription>
              </CardHeader>
              <CardContent>
                {anomaly.detected ? (
                   <p className={`text-sm flex items-start gap-2 ${anomaly.severity === 'Medium' ? 'text-yellow-600 dark:text-yellow-400' : anomaly.severity === 'High' ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                       <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${anomaly.severity === 'Medium' ? 'text-yellow-500' : anomaly.severity === 'High' ? 'text-red-500' : 'text-muted-foreground'}`} />
                       {anomaly.description}
                   </p>
                ) : (
                  <p className="text-sm text-muted-foreground">No significant anomalies detected.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
    </div>
  );
}
